// Code generated by apic; DO NOT EDIT.
import type { RestEndpointMeta } from "../types";
import type { SampleContext } from "./shared";
import { expandPath, isUnsafe, resolveAuthMode, hasMTLSLeaf } from "./shared";

// ── Go-specific helper ────────────────────────────────────────────────────────

/**
 * Produce a Go/JSON double-quoted string literal from a raw string value.
 * JSON.stringify handles ", \, control characters, etc. (F-4 appsec).
 * The result is safe to embed as a Go string literal because Go and JSON
 * share the same string-escape rules for the basic escape sequences.
 */
export function goStringLit(s: string): string {
  return JSON.stringify(s);
}

/**
 * goSample returns an idiomatic Go net/http code snippet for the given endpoint.
 *
 * Auth injection follows the same rules as curlSample:
 *  - jwt / cookie → req.Header.Set("Authorization", "Bearer <token>")
 *  - api_key      → req.Header.Set("X-API-Key", "<api-key>")
 *  - mtls/cac/piv → comment only (transport-layer TLS certificate)
 *  - public       → no auth header
 */
export function goSample(ep: RestEndpointMeta, ctx: SampleContext): string {
  const method = ep.method.toUpperCase();
  const expandedPath = expandPath(ep.path, ctx);
  const url = ctx.baseUrl.replace(/\/$/, "") + expandedPath;

  const lines: string[] = [];
  const unsafe = isUnsafe(method);

  lines.push(`package main`);
  lines.push(``);
  lines.push(`import (`);
  if (unsafe) {
    lines.push(`\t"bytes"`);
    // N-3: "encoding/json" is no longer used when we emit a raw []byte literal;
    // keep it absent to avoid an "imported and not used" compile error.
  }
  lines.push(`\t"fmt"`);
  lines.push(`\t"net/http"`);
  lines.push(`)`);
  lines.push(``);
  lines.push(`func main() {`);
  if (unsafe && ctx.body !== undefined) {
    // N-3: emit the JSON body as a Go raw-string byte slice literal so the
    // sample is valid, compilable Go. json.Marshal(<JS-literal>) was invalid.
    const bodyJSON = JSON.stringify(ctx.body, null, "\t");
    // Use a backtick-quoted Go raw string; escape any embedded backtick with
    // string concatenation (rare in practice but correct).
    const escaped = bodyJSON.replace(/`/g, "` + \"`\" + `");
    lines.push(`\tbody := []byte(\`` + escaped + `\`)`);
    lines.push(`\treq, _ := http.NewRequest("${method}", ${goStringLit(url)}, bytes.NewReader(body))`);
  } else if (unsafe) {
    lines.push(`\tbody := []byte(\`{}\`)`);
    lines.push(`\treq, _ := http.NewRequest("${method}", ${goStringLit(url)}, bytes.NewReader(body))`);
  } else {
    lines.push(`\treq, _ := http.NewRequest("${method}", ${goStringLit(url)}, nil)`);
  }

  lines.push(`\treq.Header.Set("Accept", "application/json")`);

  if (unsafe) {
    lines.push(`\treq.Header.Set("Content-Type", "application/json")`);
  }

  const mode = resolveAuthMode(ep.auth);
  const composite = ep.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(ep.auth);

  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`\treq.Header.Set("Authorization", ${goStringLit("Bearer " + tok)})`);
    if (ep.auth.csrf === "required" || ep.auth.csrf === "auto") {
      lines.push(`\treq.Header.Set("X-CSRF-Token", "<csrf-token>")`);
    }
    if (composite && mtlsLeaf) {
      lines.push(`\t// also requires mTLS client certificate (configure via tls.Config)`);
    }
  } else if (mode === "api_key") {
    const key = ctx.apiKey !== undefined && ctx.apiKey !== "" ? ctx.apiKey : "<api-key>";
    lines.push(`\treq.Header.Set("X-API-Key", ${goStringLit(key)})`);
    if (composite && mtlsLeaf) {
      lines.push(`\t// also requires mTLS client certificate (configure via tls.Config)`);
    }
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    lines.push(`\t// requires mTLS client certificate — configure via tls.Config`);
    lines.push(`\t//   cert, _ := tls.LoadX509KeyPair("client.pem", "client-key.pem")`);
    lines.push(`\t//   client := &http.Client{Transport: &http.Transport{TLSClientConfig: &tls.Config{Certificates: []tls.Certificate{cert}}}}`);
  }

  lines.push(``);
  lines.push(`\tclient := &http.Client{}`);
  lines.push(`\tresp, err := client.Do(req)`);
  lines.push(`\tif err != nil {`);
  lines.push(`\t\tpanic(err)`);
  lines.push(`\t}`);
  lines.push(`\tdefer resp.Body.Close()`);
  lines.push(`\tfmt.Println(resp.Status)`);
  lines.push(`}`);

  return lines.join("\n");
}
